import { AxiosInstance, AxiosResponse } from 'axios';
import {
  BulkItemUploadResponse,
  Category,
  CreateCategoryRequest,
  CreateItemRequest,
  Item,
  PaginatedResponse,
  SearchParams,
  UpdateCategoryRequest,
  UpdateItemRequest,
} from '../../types';

/** Item and category API calls plus item CSV import contract. */
export class CatalogApi {
  constructor(private readonly http: AxiosInstance) {}

  async getItems(): Promise<Item[]> {
    const response: AxiosResponse<Item[]> = await this.http.get('/items');
    return response.data;
  }

  async getItemsPaginated(params: SearchParams, signal?: AbortSignal): Promise<PaginatedResponse<Item>> {
    const requestConfig = signal ? { params, signal } : { params };
    const response: AxiosResponse<PaginatedResponse<Item>> = await this.http.get('/items/paginated', requestConfig);
    return response.data;
  }

  async getItem(id: string): Promise<Item> {
    const response: AxiosResponse<Item> = await this.http.get(`/items/${id}`);
    return response.data;
  }

  async getItemByBarcode(barcode: string, companyId?: string): Promise<Item> {
    const response: AxiosResponse<Item> = await this.http.get('/items/barcode', {
      params: { barcode, ...(companyId ? { companyId } : {}) },
    });
    return response.data;
  }

  async createItem(item: CreateItemRequest): Promise<Item> {
    const response: AxiosResponse<Item> = await this.http.post('/items', item);
    return response.data;
  }

  async updateItem(id: string, item: UpdateItemRequest): Promise<Item> {
    const response: AxiosResponse<Item> = await this.http.put(`/items/${id}`, item);
    return response.data;
  }

  async deleteItem(id: string): Promise<void> {
    await this.http.delete(`/items/${id}`);
  }

  async searchItems(params: SearchParams): Promise<Item[]> {
    const response: AxiosResponse<Item[]> = await this.http.get('/items/search', { params });
    return response.data;
  }

  async bulkUploadItems(file: File, companyId?: string): Promise<BulkItemUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (companyId) formData.append('companyId', companyId);
    const response: AxiosResponse<BulkItemUploadResponse> = await this.http.post('/items/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async getCategories(): Promise<Category[]> {
    const response: AxiosResponse<Category[]> = await this.http.get('/categories');
    return response.data;
  }

  async getCategoriesPaginated(params: SearchParams): Promise<PaginatedResponse<Category>> {
    const response: AxiosResponse<PaginatedResponse<Category>> = await this.http.get('/categories/paginated', { params });
    return response.data;
  }

  async getCategory(id: string): Promise<Category> {
    const response: AxiosResponse<Category> = await this.http.get(`/categories/${id}`);
    return response.data;
  }

  async createCategory(category: CreateCategoryRequest): Promise<Category> {
    const response: AxiosResponse<Category> = await this.http.post('/categories', category);
    return response.data;
  }

  async updateCategory(id: string, category: UpdateCategoryRequest): Promise<Category> {
    const response: AxiosResponse<Category> = await this.http.put(`/categories/${id}`, category);
    return response.data;
  }

  async deleteCategory(id: string): Promise<void> {
    await this.http.delete(`/categories/${id}`);
  }

}
